import { Injectable } from '@angular/core';
import { 
  Auth, 
  authState, 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut,
  updateProfile as firebaseUpdateProfile,
  User
} from '@angular/fire/auth';
import { Observable, firstValueFrom } from 'rxjs';
import { Router } from '@angular/router';
import { UserProfile } from '../interfaces/user.interface';
import { Firestore, doc, getDoc, setDoc, updateDoc } from '@angular/fire/firestore';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  currentUser$: Observable<any>;
  private readonly PARENT_PIN_KEY = 'parent_pin'; // Fallback for localStorage

  constructor(
    private auth: Auth,
    private router: Router,
    private firestore: Firestore
  ) {
    this.currentUser$ = authState(this.auth);
  }

  async getCurrentUser(): Promise<User | null> {
    return firstValueFrom(this.currentUser$);
  }

  async updateProfile(profileData: UserProfile): Promise<void> {
    const user = this.auth.currentUser;
    if (!user) throw new Error('Not authenticated');
    
    try {
      // Update Firebase Auth profile (only supports displayName and photoURL)
      await firebaseUpdateProfile(user, {
        displayName: profileData.displayName,
        photoURL: profileData.photoURL
      });

      // Store PIN in Firestore if provided
      if (profileData.pin !== undefined) {
        await this.storeParentPin(user.uid, profileData.pin);
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      throw error;
    }
  }

  public async storeParentPin(userId: string, pin: string | null): Promise<void> {
    try {
      // Store PIN in Firestore
      const userDocRef = doc(this.firestore, `users/${userId}`);
      // Ensure PIN is stored as a string
      const userData = { pin: pin ? String(pin) : null };
      
      // Check if document exists
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        // Update existing document
        await updateDoc(userDocRef, userData);
      } else {
        // Create new document
        await setDoc(userDocRef, userData);
      }
      
      // Also store in localStorage as fallback
      if (pin) {
        const encryptedPin = btoa(pin);
        localStorage.setItem(this.PARENT_PIN_KEY, encryptedPin);
      } else {
        localStorage.removeItem(this.PARENT_PIN_KEY);
      }
    } catch (error) {
      console.error('Error storing parent PIN in Firestore:', error);
      // Fall back to localStorage if Firestore fails
      if (pin) {
        const encryptedPin = btoa(pin);
        localStorage.setItem(this.PARENT_PIN_KEY, encryptedPin);
      } else {
        localStorage.removeItem(this.PARENT_PIN_KEY);
      }
    }
  }

  public async getParentPin(userId?: string): Promise<string | null> {
    if (!userId) {
      const user = await this.getCurrentUser();
      if (!user) {
        return null;
      }
      userId = user.uid;
    }
    
    try {
      // Try to get PIN from Firestore
      const userDocRef = doc(this.firestore, `users/${userId}`);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists() && userDoc.data()?.['pin']) {
        return userDoc.data()?.['pin'] as string;
      }
      
      // Fall back to localStorage if not in Firestore
      return this.getLocalPin();
    } catch (error) {
      console.error('Error getting parent PIN from Firestore:', error);
      // Fall back to localStorage
      return this.getLocalPin();
    }
  }
  
  private getLocalPin(): string | null {
    const encryptedPin = localStorage.getItem(this.PARENT_PIN_KEY);
    if (!encryptedPin) return null;
    
    try {
      return atob(encryptedPin);
    } catch (error) {
      console.error('Error decrypting PIN:', error);
      return null;
    }
  }

  public async verifyParentPin(pin: string): Promise<boolean> {
    const user = await this.getCurrentUser();
    if (!user) {
      return false;
    }
    
    try {
      const storedPin = await this.getParentPin(user.uid);
      
      // Convert both to strings for comparison to handle case where PIN is stored as a number
      const storedPinStr = storedPin ? String(storedPin) : null;
      const enteredPinStr = pin ? String(pin) : null;
      
      return storedPinStr === enteredPinStr;
    } catch (error) {
      console.error('Error verifying PIN:', error);
      return false;
    }
  }

  async login(email: string, password: string) {
    try {
      await signInWithEmailAndPassword(this.auth, email, password);
      await this.router.navigate(['/']);
    } catch (error) {
      throw error;
    }
  }

  register(email: string, password: string) {
    return createUserWithEmailAndPassword(this.auth, email, password);
  }

  async logout() {
    try {
      await signOut(this.auth);
      await this.router.navigate(['/login']);
    } catch (error) {
      throw error;
    }
  }
}